// gateway /admin/* 错误归一化。
// gateway 返 { error: 'errorCode', message?: string, retryAfter?: number }(详见 apps/gateway/src/routes/admin/*)。
// 这里把 HTTP 错误 + 网络异常都翻成 AdminApiError,让 UI 一处 try/catch 即可。

import { z } from 'zod'

export class AdminApiError extends Error {
  readonly status: number
  readonly errorCode: string
  readonly retryAfter?: number

  constructor(opts: { status: number; errorCode: string; message?: string; retryAfter?: number }) {
    super(opts.message ?? opts.errorCode)
    this.name = 'AdminApiError'
    this.status = opts.status
    this.errorCode = opts.errorCode
    this.retryAfter = opts.retryAfter
  }
}

// 把任意 unknown 翻成 AdminApiError。fetch 抛 TypeError(network)→ 包成 network_error;
// 已是 AdminApiError 原样抛;其它兜底 unknown_error。
export function toAdminApiError(err: unknown): AdminApiError {
  if (err instanceof AdminApiError) return err
  if (err instanceof TypeError) {
    // fetch 失败(断网 / DNS / CORS)统一 network_error
    return new AdminApiError({ status: 0, errorCode: 'network_error', message: err.message })
  }
  const message = err instanceof Error ? err.message : String(err)
  return new AdminApiError({ status: 0, errorCode: 'unknown_error', message })
}

// gateway 错误 body 契约;`error` 是兼容字段,`errorCode` 优先。其它字段(如 issues)允许存在但不读取。
const GatewayErrorBodySchema = z.looseObject({
  errorCode: z.string().optional(),
  error: z.string().optional(),
  message: z.string().optional(),
  retryAfter: z.number().optional()
})

// 解析 gateway 错误 body。非 JSON 或不符合契约时兜底用 fallback errorCode。
export async function parseErrorBody(
  res: Response,
  fallback: string
): Promise<{ errorCode: string; message?: string; retryAfter?: number }> {
  let body: unknown
  try {
    body = await res.json()
  } catch {
    return { errorCode: fallback }
  }
  const parsed = GatewayErrorBodySchema.safeParse(body)
  if (!parsed.success) return { errorCode: fallback }
  const { errorCode, error, message, retryAfter } = parsed.data
  return { errorCode: errorCode ?? error ?? fallback, message, retryAfter }
}
